import {
  validateAdv,
  validateMultiSelectField,
  validateRadioField,
  validateRequired,
  validateSelectField,
  validateMinValue,
  validateExtraData,
} from "components/common/services/ValidationService";
import * as CONST from "../../annotations/AdminFicheConstants";
import { CommProfileProps } from "../../annotations/AdminFicheInterface";
import { t } from "components/CentralDataMangement/translation/Translation";

interface ChildProps {
  state: any;
  dispatch: any;
  submitType: string;
}

// Helper function to parse decimal values (handles comma as decimal separator)
const parseToDecimal = (value: string | number): number => {
  if (typeof value === "number") return value;
  if (!value) return 0;
  return parseFloat(value.toString().replace(",", "."));
};

// Calculate ADV value based on the formula
const calculateADV = (
  EffectvHrsFullTime: string,
  AvrgHrsFullTime: string,
  EffectvHrs: string,
): number => {
  const fullTime = parseToDecimal(EffectvHrsFullTime);
  const avgFullTime = parseToDecimal(AvrgHrsFullTime);
  const effective = parseToDecimal(EffectvHrs);

  if (
    isNaN(fullTime) ||
    isNaN(avgFullTime) ||
    isNaN(effective) ||
    fullTime === 0
  ) {
    return 0;
  }

  // ADV formula: (Effective hours full-time - Average hours full-time) × (Effective hours / Effective hours full-time)
  const adv = (fullTime - avgFullTime) * (effective / fullTime);
  return parseFloat(adv.toFixed(2));
};

// Validate that paidAdv + unpaidAdv equals the calculated ADV
const validateAdvSum = (data: {
  paidAdv: string;
  unpaidAdv: string;
  effectiveHrsFullTime: string;
  avgHrsFullTime: string;
  effectiveHrs: string;
}): string | null => {
  const paidAdv = parseToDecimal(data.paidAdv);
  const unpaidAdv = parseToDecimal(data.unpaidAdv);
  const fullTime = parseToDecimal(data.effectiveHrsFullTime);
  const avg = parseToDecimal(data.avgHrsFullTime);
  const effectiveEmployee = parseToDecimal(data.effectiveHrs);

  if (isNaN(paidAdv) || isNaN(unpaidAdv)) {
    return t("Invalid number format");
  }

  if (fullTime <= 0) {
    return t("Number of hours/week effective employer must be greater than 0");
  }

  if (avg > fullTime) {
    return t("Average hours cannot exceed effective employer hours");
  }

  if (effectiveEmployee <= 0) {
    return t("Effective employee hours must be greater than 0");
  }

  const calculatedADV = calculateADV(
    data.effectiveHrsFullTime,
    data.avgHrsFullTime,
    data.effectiveHrs,
  );

  const sumAdv = parseFloat((paidAdv + unpaidAdv).toFixed(2));

  if (sumAdv !== calculatedADV) {
    return t(
      "Total ADV does not match the stated average and effective hours",
    );

    // return (
    //   t("Sum of paid ADV and unpaid ADV must equal the calculated ADV value") +
    //   ` (${calculatedADV})`
    // );
  }

  return null;
};

const ValidateData = ({ state, dispatch, submitType }: ChildProps) => {
  const activeTab = state.tabDetails.find((tab: any) => tab.showStatus);
  const activeTabId = activeTab?.id;

  const updateTabsStatus = (errors: object) => {
    const keysToGet = [CONST.GENERAL, CONST.SHIFT_TABLES, CONST.WAGE];
    const selectKeys = keysToGet
      .map((key) => {
        if (errors.hasOwnProperty(key)) {
          const tabIndex = findTabObjectById(key);
          if (tabIndex !== undefined && tabIndex !== null) {
            dispatch({
              type: CONST.UPDATE_TAB_ERROR,
              tabIndex,
              error: true,
            });
          }
          return tabIndex;
        }
        return null;
      })
      .filter((key) => key !== null);
    const tabIndexes = [0, 1];
    tabIndexes.forEach((index) => {
      if (!selectKeys.includes(index)) {
        dispatch({
          type: CONST.UPDATE_TAB_ERROR,
          tabIndex: index,
          error: false,
        });
      }
    });
  };

  const findTabObjectById = (idToFind: string) => {
    return state.tabDetails.findIndex((tab: any) => tab.id === idToFind);
  };

  const mandatoryFieldMapping: { [key: string]: string[] } =
    submitType === "draft"
      ? {
          general: ["syndicatedDelig", "vca"],
          shiftTables: ["name", "regime", "shift", "pc"],
        }
      : {
          general: [
            "syndicatedDelig",
            "syndicatedDeligDate",
            "agreementList",
            "vca",
            "nssoCatg",
            "catchUpRest",
            "leaves",
            "explicitRegime",
            "isKBApplicable",
            "exemptionBV",
            "catchupRestExtraData",
            "leavesExtraData"
          ],
          shiftTables: [
            "name",
            "regime",
            "shift",
            "pc",
            "EffectvHrsFullTime",
            "AvrgHrsFullTime",
            "EffectvHrs",
            "paidADV",
            "unPaidADV",
            "overtimeDay",
            "overtimeWeek",
            "advStatus",
          ],
          wage: ["isEcoChequeCp", "valueCp", "chequeTypeCp", "otherInfoCp"],
        };

  const errorsByTab: {
    [tabId: string]: { [fieldName: string]: string };
  } = {};
  state.tabDetails
    .slice(
      0,
      state.tabDetails.findIndex((tab: any) => tab.id === activeTabId) + 1,
    )
    .forEach((tab: any) => {
      const tabId = tab.id;
      let tabFields = {};

      switch (tabId) {
        case CONST.GENERAL:
          tabFields = state.general;
          break;
        case CONST.SHIFT_TABLES:
          tabFields = state.shiftTables;
          break;
        case CONST.WAGE:
          tabFields = state.wageAndPremiums;
          break;
        default:
          break;
      }
      const isPC124 = state.general.paritairBlue.some(
        (pc: any) => pc.value === 39,
      );
      const validationRules: {
        [fieldName: string]: ((value: any) => string | null | undefined)[];
      } = {
        syndicatedDelig: [validateRadioField],
        syndicatedDeligDate:
          state.general.syndicatedDelig === 1 ? [validateRadioField] : [],
        agreementList: [validateSelectField],
        vca: [validateSelectField],
        nssoCatg: isPC124 ? [validateMultiSelectField] : [],
        catchUpRest: [validateRadioField],
        leaves: [validateRadioField],
        name: [validateRequired],
        regime: [validateSelectField],
        shift: [validateSelectField],
        pc: [validateMultiSelectField],
        EffectvHrsFullTime: [validateRequired],
        AvrgHrsFullTime: [validateRequired],
        EffectvHrs: [validateRequired],
        paidADV: [validateRequired],
        unPaidADV: [validateRequired],
        // overtimeDay: [validateRequired],
        overtimeDay: [validateMinValue],
        overtimeWeek: [validateMinValue],
        explicitRegime: [validateRadioField],
        exemptionBV:
          state.general.explicitRegime === 1 ? [validateMultiSelectField] : [],
        isKBApplicable: isPC124 ? [validateRadioField] : [],
        isEcoChequeCp: [validateRadioField],
        valueCp: [validateRequired],
        chequeTypeCp: [validateSelectField],
        otherInfoCp: [validateRequired],
        catchupRestExtraData: state.general.catchUpRest === 2 || state.general.catchUpRest === 3 ? [validateExtraData] : [],
        leavesExtraData: state.general.leaves === 2 || state.general.leaves === 3 ? [validateExtraData] : [],
        // advStatus: [validateAdv],
      };

      const mandatoryFields = (mandatoryFieldMapping[tabId] || []) as string[];

      const errorsInTab: { [fieldName: string]: string } = {};
      for (const fieldName of mandatoryFields) {
        const fieldValidationRules = validationRules[fieldName];

        if (tabId === CONST.WAGE) {
          const wages = Object.values(state.wageAndPremiums || {});
          if (wages.length > 0) {
            wages.forEach((wage: any, wageIndex: number) => {
              wage.commProfile?.forEach(
                (cmProfile: CommProfileProps, profileIndex: number) => {
                  const isEcoCheque = cmProfile.isEcoChequeCp;
                  const allPossibleFields = [
                    "isEcoChequeCp",
                    "valueCp",
                    "chequeTypeCp",
                    "otherInfoCp",
                  ];

                  const fieldsToValidate: string[] = ["isEcoChequeCp"];
                  if (isEcoCheque === 1) {
                    fieldsToValidate.push("valueCp");
                  } else if (isEcoCheque === 2) {
                    fieldsToValidate.push("chequeTypeCp");
                    if (
                      cmProfile.chequeTypeCp &&
                      cmProfile.chequeTypeCp?.value === 5
                    ) {
                      fieldsToValidate.push("otherInfoCp");
                    }
                  }
                  // Ensure any field not in fieldsToValidate is reset
                  const fieldsToReset = allPossibleFields.filter(
                    (f) => !fieldsToValidate.includes(f),
                  );
                  fieldsToReset.forEach((fieldName) => {
                    dispatch({
                      type: CONST.UPDATE_WAGE_FIELD_DATA,
                      field: fieldName + "Error",
                      value: "",
                      wageId: wageIndex,
                      profileId: profileIndex,
                    });
                  });
                  fieldsToValidate.forEach((fieldName) => {
                    const fieldValue =
                      cmProfile[fieldName as keyof CommProfileProps];
                    const fieldValidationRules = validationRules[fieldName];

                    if (fieldValidationRules) {
                      const error = fieldValidationRules
                        .map((rule) => rule(fieldValue))
                        .find((result) => result);

                      dispatch({
                        type: CONST.UPDATE_WAGE_FIELD_DATA,
                        field: `${fieldName}Error`,
                        value: error || "",
                        wageId: wageIndex,
                        profileId: profileIndex,
                      });

                      if (error) {
                        errorsInTab[fieldName] = error;
                      }
                    }
                  });
                },
              );
            });
          }
        } else if (
          tabId === CONST.SHIFT_TABLES &&
          state.shiftTables.length > 0
        ) {
          state.shiftTables.forEach((table: any, index: number) => {
            let fieldValue: any = table[fieldName as keyof typeof table];
            const tableErrors: string[] = [];

            if (fieldName === "advStatus") {
              fieldValue = {
                paidAdv: table.paidADV,
                unpaidAdv: table.unPaidADV,
                effectiveHrs: table.EffectvHrsFullTime,
                avgHrs: table.AvrgHrsFullTime,
              };
            }

            // Validate ADV sum when validating advStatus or unPaidADV
            if (fieldName === "advStatus" || fieldName === "unPaidADV") {
              const advSumError = validateAdvSum({
                paidAdv: table.paidADV,
                unpaidAdv: table.unPaidADV,
                effectiveHrsFullTime: table.EffectvHrsFullTime,
                avgHrsFullTime: table.AvrgHrsFullTime,
                effectiveHrs: table.EffectvHrs,
              });

              if (advSumError) {
                errorsInTab["advStatus"] = advSumError;
                dispatch({
                  type: CONST.UPDATE_SHIFT_DATA,
                  field: "advStatusError",
                  value: advSumError,
                  tableId: index,
                });
              } else {
                dispatch({
                  type: CONST.UPDATE_SHIFT_DATA,
                  field: "advStatusError",
                  value: "",
                  tableId: index,
                });
              }
            }

            if (fieldValidationRules) {
              for (const rule of fieldValidationRules) {
                const validationError = rule(fieldValue as any);

                if (validationError) {
                  tableErrors.push(validationError);
                  errorsInTab[fieldName] = validationError;
                  dispatch({
                    type: CONST.UPDATE_SHIFT_DATA,
                    field: fieldName + "Error",
                    value: validationError,
                    tableId: index,
                  });
                  break;
                } else {
                  dispatch({
                    type: CONST.UPDATE_SHIFT_DATA,
                    field: fieldName + "Error",
                    value: "",
                    tableId: index,
                  });
                }
              }
            }
          });
        } else if (tabId === CONST.GENERAL) {
          const fieldValue = tabFields[fieldName as keyof typeof tabFields];
          if (fieldValidationRules) {
            for (const rule of fieldValidationRules) {
              const validationError = rule(fieldValue);
              if (validationError) {
                errorsInTab[fieldName] = validationError;
                dispatch({
                  type: CONST.UPDATE_FIELD_ERROR,
                  field: fieldName,
                  error: validationError,
                });
                break;
              } else {
                dispatch({
                  type: CONST.UPDATE_FIELD_ERROR,
                  field: fieldName,
                  error: "",
                });
              }
            }
          }
        }
      }

      if (Object.keys(errorsInTab).length > 0) {
        errorsByTab[tabId] = errorsInTab;
        return false;
      }
    });
  updateTabsStatus(errorsByTab);
  if (Object.keys(errorsByTab).length > 0) {
    return false;
  }

  return true;
};

export default ValidateData;
